import { useState, useEffect, useMemo, useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Sidenav from 'examples/Sidenav';
import theme from 'assets/theme';
import themeDark from 'assets/theme-dark';
import { useArgonController, setMiniSidenav } from 'context';
import brand from 'assets/images/frankly-small.png';
import 'assets/css/nucleo-icons.css';
import 'assets/css/nucleo-svg.css';

// --- REFACTORED IMPORTS ---
import { UserContext } from 'context/userContext';
import { OrgContext } from 'context/orgContext';
import { getStatus } from 'services/user'; // The new, secure way to check auth
import { userOrgs } from 'services/org'; // Assuming this service is also updated
import generateRoutes from 'routes';

import CircularProgress from '@mui/material/CircularProgress'; // <-- Import this
import Box from '@mui/material/Box'; // <-- Import Box for easy centering

export default function App() {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, layout, sidenavColor, darkSidenav, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const { userId, setUserId } = useContext(UserContext);
  const { setOrgs } = useContext(OrgContext);

  // --- REFACTORED STATE MANAGEMENT ---
  // isLoading: true while we check for a session, prevents rendering the wrong routes.
  // isAuthenticated: derived from the session check.
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // This function will run on initial load and whenever `userId` changes.
    const manageAuthentication = async () => {
      // If we have a userId in our context, it means the user has just logged in.
      // We can trust this and fetch their data.
      if (userId) {
        setIsAuthenticated(true);
        setIsLoading(true); // Show loading while we fetch orgs
        try {
          const { orgs: fetchedOrgs } = await userOrgs();
          if (fetchedOrgs) {
            setOrgs(fetchedOrgs);
          }
        } catch (error) {
          console.error('Failed to fetch orgs after login:', error);
          // Handle case where login succeeded but fetching orgs failed
          setOrgs(null);
        } finally {
          setIsLoading(false);
        }
        return; // Stop here, we are done.
      }

      // If we DON'T have a userId, this might be the initial page load.
      // Let's check the server for an existing session.
      try {
        const { user } = await getStatus();
        if (user && user.id) {
          // A session exists! Set the user ID in our context.
          // This will cause this useEffect to run AGAIN, but this time
          // it will enter the `if (userId)` block above and fetch the orgs.
          setUserId(user.id);
          // We don't set loading to false here, we let the next run handle it.
        } else {
          // No session exists on the server.
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        // getStatus() failed (401), meaning no session. This is a normal outcome.
        console.log('No active session found on initial load.');
        setIsAuthenticated(false);
        setUserId(null);
        setOrgs(null);
        setIsLoading(false);
      }
    };

    manageAuthentication();
  }, [userId, setUserId, setOrgs]); // This effect now correctly depends on `userId`.

  // Generate routes based on the final authentication state.
  const appRoutes = useMemo(() => generateRoutes(isAuthenticated), [isAuthenticated]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  // --- REFACTORED RENDER LOGIC ---
  // While checking for auth, show a loading spinner to prevent screen flicker.
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh', // Take up the full viewport height
          width: '100vw', // Take up the full viewport width
        }}
      >
        <CircularProgress color="info" />
      </Box>
    );
  }
  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* The Sidenav should only render if the layout is 'dashboard' AND the user is authenticated */}
      {layout === 'dashboard' && isAuthenticated && (
        <Sidenav
          color={sidenavColor}
          brand={darkSidenav || darkMode ? brand : brand}
          brandName="LifeCycle"
          routes={appRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Routes>
        {getRoutes(appRoutes)}
        {/* This is the final fallback. If no route matches, it will redirect. */}
        <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/authentication/sign-in'} />} />
      </Routes>
    </ThemeProvider>
  );
}
